import { AnimatePresence, motion, useScroll, useSpring, useTransform } from 'framer-motion';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { BrandSitesV2Theme } from '../common/GlobalStyle';
import useBreakpoints from '../hooks/useBreakpoints';

export default function StorySection2() {
  const media = useBreakpoints({ defaultMedia: 'desktop' });
  const containerRef = useRef<HTMLDivElement>(null);
  const fakeImageRef = useRef<HTMLImageElement>(null);

  const [imageWidth, setImageWidth] = useState(0);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end end'],
  });

  const scale = useSpring(useTransform(scrollYProgress, [0, 0.8], [1, 1.1]), {
    stiffness: 600,
    damping: 100,
  });

  const calcContainerPadding = (_media: typeof media) =>
    (document.body.clientWidth - Number(BrandSitesV2Theme!.brandSitesV2.containerWidths[_media].replace('px', ''))) / 2;

  useEffect(() => {
    const calcFakeImageWidth = () => {
      let paddingLeft = calcContainerPadding(media);
      if (media === 'mobile') {
        paddingLeft = Math.min(24, paddingLeft);
        paddingLeft = Math.max(calcContainerPadding('tablet'), paddingLeft);
      }

      let width = document.body.clientWidth - paddingLeft;
      setImageWidth(width);
    };

    calcFakeImageWidth();
    window.addEventListener('resize', calcFakeImageWidth);
    return () => {
      window.removeEventListener('resize', calcFakeImageWidth);
    };
  }, [media]);

  return (
    <AnimatePresence>
      <Container ref={containerRef}>
        <OuterWrapper>
          <InnerWrapper>
            <TitleWrapper>
              <FakeTitle>청연의 다양한 이야기를 만나보세요.</FakeTitle>
              <TitleDesktop>{TitleDesktopSvg}</TitleDesktop>
              <TitleTablet>{TitleTabletSvg}</TitleTablet>
            </TitleWrapper>
            <Content>
              <Description>청연의 이야기와 청연을 만들어가는 사람들의 더 많은 이야기를 만나보세요.</Description>
              <Link href="/about" passHref>
                <Button>청연이야기 더보기</Button>
              </Link>
            </Content>
          </InnerWrapper>
        </OuterWrapper>
        <ImageWrapper $width={imageWidth}>
          <picture>
            <source
              type="image/webp"
              srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/home/webp/story2_v2.webp"
            />
            <source type="image/png" srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/home/story2_v2.png" />
            <Image
              style={{ scale }}
              ref={fakeImageRef}
              src="https://cleanhome-web.cleaninglab.co.kr/brandsite/home/story2_v2.png"
              alt=""
            />
          </picture>
        </ImageWrapper>
      </Container>
    </AnimatePresence>
  );
}

const Container = styled.section`
  position: relative;
  width: 100%;
  background: #e9f7f8;
  overflow: hidden;
  padding-top: 80px;
  padding-bottom: 100px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding-top: 56px;
    padding-bottom: 80px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    padding-top: 74px;
    padding-bottom: 50px;
  }
`;

const OuterWrapper = styled.div`
  position: relative;
  padding: 0 24px;
  margin-bottom: 80px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    margin-bottom: 60px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-bottom: 52px;
  }
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 0 auto;

  max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.desktop};

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.tablet};
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    /* max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.mobile}; */
    flex-direction: column;
  }
`;

const TitleWrapper = styled.div`
  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-bottom: 18px;
  }
`;

const FakeTitle = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  margin: -1px;
  clip: rect(0, 0, 0, 0);
  text-indent: -5000px;
`;

const TitleDesktop = styled.div`
  display: block;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: none;
  }
`;

const TitleTablet = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: block;
  }
`;

const Content = styled.div``;

const Description = styled.h2`
  word-break: keep-all;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 300;
  font-size: 21px;
  line-height: 140%;
  margin-bottom: 40px;
  width: 486px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    font-size: 17px;
    margin-bottom: 30px;
    width: 405px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-bottom: 44px;
    width: auto;
    font-weight: 400;
  }
`;

const Button = styled.a`
  display: inline-block;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  border: 1px solid #999999;
  border-radius: 8px;
  padding: 12px 20px;
  transition: 0.2s;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding: 10px 16px;
    font-size: 15px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    padding: 9px 16px;
  }

  &:hover {
    color: #ffffff;
    background: #42484c;
    border-color: #42484c;
  }
`;

const ImageWrapper = styled.div<{ $width: number }>`
  width: ${({ $width }) => $width}px;
  height: 440px;
  overflow: hidden;
  margin-left: auto;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    height: 340px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    height: ${({ $width }) => Math.round(Math.max($width / 2, 200))}px;
  }
`;

const Image = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 8%;
`;

const TitleDesktopSvg = (
  <svg width="436" height="103" viewBox="0 0 436 103" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M19.8859 5.214V0.843999H6.72989V5.214H19.8859ZM30.9719 35.804C30.9719 38.748 28.3959 40.588 24.5779 40.588C20.8979 40.588 18.2759 38.748 18.2759 35.804C18.2759 32.86 20.8979 31.02 24.5779 31.02C28.3959 31.02 30.9719 32.86 30.9719 35.804ZM36.6759 26.144V0.614H31.5699V13.862H24.3019V18.14H31.5699V26.144H36.6759ZM10.5479 12.206C10.4559 13.31 10.2259 14.276 9.81189 15.15C8.24789 18.692 4.70589 20.808 0.749891 22.326L3.14189 26.374C7.69589 25.04 12.0199 21.36 13.4459 18.048C14.9179 21.314 19.5639 24.304 23.6119 25.546L25.8199 21.406C21.7259 20.302 18.3219 18.14 16.9419 14.874C16.6199 14.092 16.3899 13.172 16.2979 12.206H24.6239V7.882H1.94589V12.206H10.5479ZM36.1239 35.804C36.1239 30.514 31.3399 26.696 24.5779 26.696C17.9539 26.696 13.1239 30.514 13.1239 35.804C13.1239 41.186 17.9539 44.958 24.5779 44.958C31.3399 44.958 36.1239 41.186 36.1239 35.804ZM72.9203 6.502H62.1563C60.1323 4.386 57.1883 3.052 53.7383 3.052C47.2063 3.052 42.5143 7.698 42.5143 13.632C42.5143 19.704 47.2063 24.212 53.7383 24.212C57.4183 24.212 60.5463 22.786 62.5243 20.394H72.9203V30.284H78.0723V0.705998H72.9203V6.502ZM78.6703 42.934V38.518H57.8783V27.8H52.7723V42.934H78.6703ZM59.9023 13.586C59.9023 17.082 57.2343 19.796 53.7383 19.796C50.1503 19.796 47.5743 17.082 47.5743 13.586C47.5743 10.09 50.1503 7.514 53.7383 7.514C57.2343 7.514 59.9023 10.09 59.9023 13.586ZM64.9623 13.632C64.9623 12.758 64.8243 11.838 64.6403 11.01H72.9203V15.978H64.7323C64.8703 15.242 64.9623 14.46 64.9623 13.632ZM90.4427 12.114C90.4427 8.986 92.9727 6.41 96.4227 6.41C99.8727 6.41 102.449 8.986 102.449 12.114C102.449 15.242 99.8727 17.818 96.4227 17.818C92.9727 17.818 90.4427 15.242 90.4427 12.114ZM111.051 25.408C107.785 25.776 101.161 26.374 96.3307 26.512C94.3067 26.558 91.7767 26.604 89.3847 26.604H83.1287L83.6807 31.158C87.6367 31.158 93.3407 31.02 97.2967 30.79C101.667 30.652 107.923 30.146 111.419 29.548L111.051 25.408ZM113.627 39.116H118.779V0.751998H113.627V39.116ZM85.3827 12.114C85.3827 17.818 90.0287 22.096 96.4227 22.096C102.955 22.096 107.601 17.818 107.601 12.114C107.601 6.41 102.863 2.132 96.4227 2.132C90.0287 2.132 85.3827 6.41 85.3827 12.114ZM137.97 28.122C146.158 28.49 155.22 28.076 161.89 26.88L161.384 22.556C155.956 23.66 148.182 23.798 143.122 23.568V7.698H157.566V3.328H137.97V28.122ZM169.848 0.751998H164.696V39.07H169.848V17.588H176.84V13.218H169.848V0.751998ZM206.276 34.7C206.276 37.966 203.562 40.128 199.882 40.128C196.064 40.128 193.488 37.966 193.488 34.7C193.488 31.434 196.064 29.318 199.882 29.318C203.562 29.318 206.276 31.434 206.276 34.7ZM183.276 12.942C183.276 9.63 185.76 7.054 189.21 7.054C192.706 7.054 195.144 9.63 195.144 12.942C195.144 16.116 192.706 18.784 189.21 18.784C185.76 18.784 183.276 16.116 183.276 12.942ZM217.684 19.106V14.782H211.336V10.55H217.684V6.134H211.336V0.659999H206.23V24.718H211.336V19.106H217.684ZM211.336 34.7C211.336 29.226 206.506 24.948 199.882 24.948C193.166 24.948 188.382 29.226 188.382 34.7C188.382 40.588 193.166 44.498 199.882 44.498C206.506 44.498 211.336 40.588 211.336 34.7ZM178.216 12.942C178.216 18.692 182.77 23.154 189.21 23.154C195.604 23.154 200.204 18.692 200.204 12.942C200.204 7.1 195.604 2.638 189.21 2.638C182.77 2.638 178.216 7.1 178.216 12.942ZM253.882 43.486V39.116H233.55V31.572H228.444V43.486H253.882ZM243.808 12.436V8.112H219.336V12.436H243.808ZM237.69 5.858V1.442H225.96V5.858H237.69ZM236.448 21.866C236.448 23.936 234.7 25.546 231.94 25.546C229.18 25.546 227.34 23.936 227.34 21.866C227.34 19.796 229.18 18.186 231.94 18.186C234.7 18.186 236.448 19.796 236.448 21.866ZM241.416 21.82C241.416 17.128 237.276 14.046 231.94 14.046C226.558 14.046 222.234 17.128 222.234 21.82C222.234 26.65 226.558 29.548 231.94 29.548C237.138 29.548 241.416 26.65 241.416 21.82ZM259.632 18.094V13.632H252.686V0.705998H247.626V32.722H252.686V18.094H259.632ZM273.763 15.058C273.763 22.096 278.639 26.834 285.355 26.834C292.025 26.834 296.948 22.096 296.948 15.058C296.948 8.02 292.025 3.236 285.355 3.236C278.639 3.236 273.763 8.02 273.763 15.058ZM278.961 15.012C278.961 10.55 281.629 7.836 285.355 7.836C288.989 7.836 291.749 10.55 291.749 15.012C291.749 19.474 289.036 22.188 285.355 22.188C281.629 22.188 278.961 19.474 278.961 15.012ZM303.388 39.07H308.494V0.751998H303.388V39.07ZM319.392 15.012C319.392 10.458 322.06 7.744 325.74 7.744C329.282 7.744 332.18 10.504 332.18 15.012C332.18 19.566 329.282 22.28 325.74 22.28C322.06 22.28 319.392 19.566 319.392 15.012ZM354.398 23.292V18.83H347.452V12.712H354.398V8.296H347.452V0.751998H342.392V39.07H347.452V23.292H354.398ZM314.24 15.058C314.24 22.188 319.07 26.972 325.74 26.972C332.456 26.972 337.332 22.188 337.332 15.058C337.332 7.974 332.456 3.006 325.74 3.006C319.07 3.006 314.24 7.974 314.24 15.058ZM358.902 3.42V7.882H373.53C372.932 15.15 366.906 22.326 356.832 26.006L359.776 30.192C365.802 27.754 370.54 24.304 373.944 19.842C377.348 15.38 379.05 9.906 379.096 3.42H358.902ZM386.088 39.07H391.24V0.751998H386.088V39.07ZM430.475 44.59V40.404H408.073V37.736H429.325V26.742H403.105V31.02H424.357V33.596H403.105V44.59H430.475ZM435.627 24.58V20.44H396.895V24.58H435.627ZM430.061 17.956V13.862H408.395V11.286H429.049V0.659999H403.335V4.846H424.035V7.238H403.335V17.956H430.061ZM34.9279 102.934V98.518H14.5039V87.8H9.35189V102.934H34.9279ZM16.7119 78.922H6.40789V67.33H16.7119V78.922ZM21.7259 83.292V62.96H1.25589V83.292H21.7259ZM40.6779 75.886V71.516H33.6859V60.706H28.5799V90.928H33.6859V75.886H40.6779ZM66.5723 81.59C61.0523 82.648 55.0723 83.338 48.4943 83.2V63.006H43.3423V87.524C53.0483 87.524 60.5923 87.064 67.0323 85.73L66.5723 81.59ZM82.0743 77.634V73.264H75.0823V60.752H69.9303V99.07H75.0823V77.634H82.0743ZM110.637 79.198H94.3527V72.942H110.637V79.198ZM100.103 83.568V90.606H83.1747V94.93H121.907V90.606H105.163V83.568H115.697V62.316H110.637V68.572H94.3527V62.316H89.2467V83.568H100.103ZM145.639 84.718C140.993 81.912 137.451 76.898 137.451 67.882V62.546H132.207V67.882C132.207 77.082 128.711 82.28 123.789 85.73L126.917 89.502C130.551 87.248 133.725 82.694 134.829 78.646C136.071 82.418 139.291 86.604 142.419 88.49L145.639 84.718ZM156.357 99.116H161.417V60.752H156.357V99.116ZM152.677 97.644V61.304H147.709V72.252H141.775V76.53H147.709V97.644H152.677ZM177.606 72.574C177.606 68.664 180.872 66.088 185.334 66.088C189.704 66.088 193.108 68.664 193.108 72.574C193.108 76.622 189.704 79.06 185.334 79.06C180.872 79.06 177.606 76.622 177.606 72.574ZM204.7 94.976V90.56H194.856V83.89H189.796V90.56H180.872V83.89H175.812V90.56H165.968V94.976H204.7ZM172.408 72.574C172.408 78.83 177.698 83.522 185.334 83.522C192.97 83.522 198.306 78.83 198.306 72.574C198.306 66.318 192.97 61.534 185.334 61.534C177.698 61.534 172.408 66.318 172.408 72.574ZM208.514 98.748H214.172V93.32H208.514V98.748Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);

const TitleTabletSvg = (
  <svg width="263" height="64" viewBox="0 0 263 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.1533 3.343V0.778H4.43134V3.343H12.1533ZM18.6603 21.298C18.6603 23.026 17.1483 24.106 14.9073 24.106C12.7473 24.106 11.2083 23.026 11.2083 21.298C11.2083 19.57 12.7473 18.49 14.9073 18.49C17.1483 18.49 18.6603 19.57 18.6603 21.298ZM22.0083 15.628V0.643H19.0113V8.419H14.7453V10.93H19.0113V15.628H22.0083ZM6.67234 7.447C6.61834 8.095 6.48334 8.662 6.24034 9.175C5.32234 11.254 3.24334 12.496 0.921344 13.387L2.32534 15.763C4.99834 14.98 7.53634 12.82 8.37334 10.876C9.23734 12.793 11.9643 14.548 14.3403 15.277L15.6363 12.847C13.2333 12.199 11.2353 10.93 10.4253 9.013C10.2363 8.554 10.1013 8.014 10.0473 7.447H14.9343V4.909H1.62334V7.447H6.67234ZM21.6843 21.298C21.6843 18.193 18.8763 15.952 14.9073 15.952C11.0193 15.952 8.18434 18.193 8.18434 21.298C8.18434 24.457 11.0193 26.671 14.9073 26.671C18.8763 26.671 21.6843 24.457 21.6843 21.298ZM43.8222 4.099H37.5042C36.3162 2.857 34.5882 2.074 32.5632 2.074C28.7292 2.074 25.9752 4.801 25.9752 8.284C25.9752 11.848 28.7292 14.494 32.5632 14.494C34.7232 14.494 36.5592 13.657 37.7202 12.253H43.8222V18.058H46.8462V0.696999H43.8222V4.099ZM47.1972 25.483V22.891H34.9932V16.6H31.9962V25.483H47.1972ZM36.1812 8.257C36.1812 10.309 34.6152 11.902 32.5632 11.902C30.4572 11.902 28.9452 10.309 28.9452 8.257C28.9452 6.205 30.4572 4.693 32.5632 4.693C34.6152 4.693 36.1812 6.205 36.1812 8.257ZM39.1512 8.284C39.1512 7.771 39.0702 7.231 38.9622 6.745H43.8222V9.661H39.0162C39.0972 9.229 39.1512 8.77 39.1512 8.284ZM54.6471 7.393C54.6471 5.557 56.1321 4.045 58.1571 4.045C60.1821 4.045 61.6941 5.557 61.6941 7.393C61.6941 9.229 60.1821 10.741 58.1571 10.741C56.1321 10.741 54.6471 9.229 54.6471 7.393ZM66.7431 15.196C64.8261 15.412 60.9381 15.763 58.1031 15.844C56.9151 15.871 55.4301 15.898 54.0261 15.898H50.3541L50.6781 18.571C53.0001 18.571 56.3481 18.49 58.6701 18.355C61.2351 18.274 64.9071 17.977 66.9591 17.626L66.7431 15.196ZM68.2551 23.242H71.2791V0.723999H68.2551V23.242ZM51.6771 7.393C51.6771 10.741 54.4041 13.252 58.1571 13.252C61.9911 13.252 64.7181 10.741 64.7181 7.393C64.7181 4.045 61.9371 1.534 58.1571 1.534C54.4041 1.534 51.6771 4.045 51.6771 7.393ZM83.6234 16.789C88.4294 17.005 93.7484 16.762 97.6634 16.06L97.3664 13.522C94.1804 14.17 89.6174 14.251 86.6474 14.116V4.801H95.1254V2.236H83.6234V16.789ZM102.334 0.723999H99.3104V23.215H102.334V10.606H106.438V8.041H102.334V0.723999ZM124.256 20.65C124.256 22.567 122.663 23.836 120.503 23.836C118.262 23.836 116.75 22.567 116.75 20.65C116.75 18.733 118.262 17.491 120.503 17.491C122.663 17.491 124.256 18.733 124.256 20.65ZM110.756 7.879C110.756 5.935 112.214 4.423 114.239 4.423C116.291 4.423 117.722 5.935 117.722 7.879C117.722 9.742 116.291 11.308 114.239 11.308C112.214 11.308 110.756 9.742 110.756 7.879ZM130.952 11.497V8.959H127.226V6.475H130.952V3.883H127.226V0.669999H124.229V14.791H127.226V11.497H130.952ZM127.226 20.65C127.226 17.437 124.391 14.926 120.503 14.926C116.561 14.926 113.753 17.437 113.753 20.65C113.753 24.106 116.561 26.401 120.503 26.401C124.391 26.401 127.226 24.106 127.226 20.65ZM107.786 7.879C107.786 11.254 110.459 13.873 114.239 13.873C117.992 13.873 120.692 11.254 120.692 7.879C120.692 4.45 117.992 1.831 114.239 1.831C110.459 1.831 107.786 4.45 107.786 7.879ZM152.739 25.807V23.242H140.805V18.814H137.808V25.807H152.739ZM146.826 7.582V5.044H132.462V7.582H146.826ZM143.235 3.721V1.129H136.35V3.721H143.235ZM142.506 13.117C142.506 14.332 141.48 15.277 139.86 15.277C138.24 15.277 137.16 14.332 137.16 13.117C137.16 11.902 138.24 10.957 139.86 10.957C141.48 10.957 142.506 11.902 142.506 13.117ZM145.422 13.09C145.422 10.336 142.992 8.527 139.86 8.527C136.701 8.527 134.163 10.336 134.163 13.09C134.163 15.925 136.701 17.626 139.86 17.626C142.911 17.626 145.422 15.925 145.422 13.09ZM156.114 10.903V8.284H152.037V0.696999H149.067V19.489H152.037V10.903H156.114ZM165.488 9.121C165.488 13.252 168.35 16.033 172.292 16.033C176.207 16.033 179.096 13.252 179.096 9.121C179.096 4.99 176.207 2.182 172.292 2.182C168.35 2.182 165.488 4.99 165.488 9.121ZM168.539 9.094C168.539 6.475 170.105 4.882 172.292 4.882C174.425 4.882 176.045 6.475 176.045 9.094C176.045 11.713 174.452 13.306 172.292 13.306C170.105 13.306 168.539 11.713 168.539 9.094ZM182.876 23.215H185.873V0.723999H182.876V23.215ZM192.81 9.094C192.81 6.421 194.376 4.828 196.536 4.828C198.615 4.828 200.316 6.448 200.316 9.094C200.316 11.767 198.615 13.36 196.536 13.36C194.376 13.36 192.81 11.767 192.81 9.094ZM213.357 13.954V11.335H209.28V7.744H213.357V5.152H209.28V0.723999H206.31V23.215H209.28V13.954H213.357ZM189.786 9.121C189.786 13.306 192.621 16.114 196.536 16.114C200.478 16.114 203.34 13.306 203.34 9.121C203.34 4.963 200.478 2.047 196.536 2.047C192.621 2.047 189.786 4.963 189.786 9.121ZM216.541 2.29V4.909H225.127C224.776 9.175 221.239 13.387 215.326 15.547L217.054 18.004C220.591 16.573 223.372 14.548 225.37 11.929C227.368 9.31 228.367 6.097 228.394 2.29H216.541ZM232.498 23.215H235.522V0.723999H232.498V23.215ZM259.091 26.455V23.998H245.942V22.432H258.416V15.979H243.026V18.49H255.5V20.002H243.026V26.455H259.091ZM262.115 14.71V12.28H239.381V14.71H262.115ZM258.848 10.822V8.419H246.131V6.907H258.254V0.669999H243.161V3.127H255.311V4.531H243.161V10.822H258.848ZM20.9823 63.483V60.891H8.99434V54.6H5.97034V63.483H20.9823ZM10.2903 49.389H4.24234V42.585H10.2903V49.389ZM13.2333 51.954V40.02H1.21834V51.954H13.2333ZM24.3573 47.607V45.042H20.2533V38.697H17.2563V56.436H20.2533V47.607H24.3573ZM40.0962 50.955C36.8562 51.576 33.3462 51.981 29.4852 51.9V40.047H26.4612V54.438C32.1582 54.438 36.5862 54.168 40.3662 53.385L40.0962 50.955ZM49.1952 48.633V46.068H45.0912V38.724H42.0672V61.215H45.0912V48.633H49.1952ZM66.5001 49.551H56.9421V45.879H66.5001V49.551ZM60.3171 52.116V56.247H50.3811V58.785H73.1151V56.247H63.2871V52.116H69.4701V39.642H66.5001V43.314H56.9421V39.642H53.9451V52.116H60.3171ZM87.585 52.791C84.858 51.144 82.779 48.201 82.779 42.909V39.777H79.701V42.909C79.701 48.309 77.649 51.36 74.76 53.385L76.596 55.599C78.729 54.276 80.592 51.603 81.24 49.227C81.969 51.441 83.859 53.898 85.695 55.005L87.585 52.791ZM93.876 61.242H96.846V38.724H93.876V61.242ZM91.716 60.378V39.048H88.8V45.474H85.317V47.985H88.8V60.378H91.716ZM106.888 45.663C106.888 43.368 108.805 41.856 111.424 41.856C113.989 41.856 115.987 43.368 115.987 45.663C115.987 48.039 113.989 49.47 111.424 49.47C108.805 49.47 106.888 48.039 106.888 45.663ZM122.791 58.812V56.22H117.013V52.305H114.043V56.22H108.805V52.305H105.835V56.22H100.057V58.812H122.791ZM103.837 45.663C103.837 49.335 106.942 52.089 111.424 52.089C115.906 52.089 119.038 49.335 119.038 45.663C119.038 41.991 115.906 39.183 111.424 39.183C106.942 39.183 103.837 41.991 103.837 45.663ZM125.57 61.026H128.891V57.84H125.57V61.026Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);
